/** 推送数据分析：以消息创建 cohort 取真值，逻辑消息与设备投递分别呈现。 */
import {
  PUSH_TEMPLATE_CATEGORIES,
  PushAnalyticsQuerySchema,
  validatePushAnalyticsWindow,
} from '@novel-admin/shared';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import {
  type PushAnalyticsMetricCounts,
  type PushAnalyticsQuery,
  usePushAnalytics,
  usePushAnalyticsNotifications,
  usePushTemplates,
} from '~/data/queries';
import {
  buildPushAnalyticsWindow,
  defaultPushAnalyticsRange,
  pushAnalyticsApi,
  pushAnalyticsCalendarToday,
} from '~/hooks/use-push-analytics';
import { fmtDateTimeInTimeZone, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { formatPushTemplateName } from '~/lib/push-template-display';
import { DateRangeButton } from '~/ui/DatePicker';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { StatCard } from '~/ui/StatCard';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { Charts } from '~/ui/charts';
import {
  DELIVERY_STATUS_LABELS,
  MESSAGE_STATUS_LABELS,
  PUSH_ANALYTICS_CATEGORY_LABELS,
  PushAnalyticsNotificationDetail,
  TEST_KIND_LABELS,
  deviceDataLabel,
} from './PushAnalyticsNotificationDetail';

const DEVICE_FILTERS = [
  { key: 'platform', label: '设备平台', values: ['IOS', 'ANDROID'] },
  { key: 'vendor', label: '设备供应商', values: ['APNS', 'FCM'] },
  { key: 'delivery_status', label: '设备当前状态', values: Object.keys(DELIVERY_STATUS_LABELS) },
] as const;
const timezoneOptions = (() => {
  const supported = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  return [
    ...new Set([
      'UTC',
      'Asia/Taipei',
      'America/New_York',
      'Europe/Berlin',
      ...(supported.supportedValuesOf?.('timeZone') ?? []),
    ]),
  ];
})();
const ratio = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(2)}%`);
const number = (value: number) => value.toLocaleString();
const metrics: Array<{ key: keyof PushAnalyticsMetricCounts; label: string; share?: boolean }> = [
  { key: 'message_created', label: '创建消息' },
  { key: 'message_dispatched', label: '逻辑分派' },
  { key: 'message_opened', label: '已记录打开' },
  { key: 'dispatched_opened', label: '已分派且记录打开' },
  { key: 'opened_without_dispatch', label: '无分派却记录打开' },
  { key: 'open_rate', label: '分派消息打开率', share: true },
  { key: 'device_total', label: '设备投递记录' },
  { key: 'provider_accepted', label: '供应商接受' },
  { key: 'provider_acceptance_share', label: '供应商接受占比', share: true },
  { key: 'device_pending', label: '设备排队' },
  { key: 'device_sending', label: '设备发送中' },
  { key: 'device_failed_retriable', label: '设备可重试失败' },
  { key: 'device_failed_permanent', label: '设备永久失败' },
];

function initialQuery(): PushAnalyticsQuery {
  const now = new Date();
  return {
    ...buildPushAnalyticsWindow(defaultPushAnalyticsRange(now), 'UTC', now),
    report_timezone: 'UTC',
    include_test_data: false,
  };
}

export function PushAnalyticsPanel() {
  const appId = useAppScope((state) => state.appId);
  const [range, setRange] = useState<[Date, Date]>(defaultPushAnalyticsRange);
  const [draft, setDraft] = useState<Record<string, string>>({ report_timezone: 'UTC' });
  const [includeTests, setIncludeTests] = useState(false);
  const [applied, setApplied] = useState<PushAnalyticsQuery>(initialQuery);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<{
    id: string;
    scope: string;
  } | null>(null);
  const selectedId = selectedNotification?.scope === appId ? selectedNotification.id : null;
  const query: PushAnalyticsQuery = { ...applied, ...(appId !== 'all' ? { app_id: appId } : {}) };
  const signature = JSON.stringify(query);
  const [paging, setPaging] = useState({ signature: '', page: 1, limit: 20 });
  const page = paging.signature === signature ? paging.page : 1;
  const overview = usePushAnalytics(query);
  const notifications = usePushAnalyticsNotifications({ ...query, page, limit: paging.limit });
  const data = overview.data;
  const { data: templates } = usePushTemplates();
  // 不按启停态过滤：历史消息仍可能属于已停用或已删除的模板。
  const templateSlugs = [
    ...new Set([
      ...templates.map((template) => template.slug),
      ...(data?.templates.map((template) => template.template_slug) ?? []),
      ...(draft.template_slug ? [draft.template_slug] : []),
    ]),
  ];
  const time = (value: string | null) =>
    value ? fmtDateTimeInTimeZone(value, query.report_timezone) : '—';
  const setField = (key: string, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const applyFilters = () => {
    try {
      const parsed = PushAnalyticsQuerySchema.safeParse({
        ...Object.fromEntries(
          Object.entries(draft)
            .filter(([, value]) => value.trim() !== '')
            .map(([key, value]) => [key, value.trim()]),
        ),
        ...buildPushAnalyticsWindow(range, draft.report_timezone || 'UTC', new Date()),
        include_test_data: includeTests,
      });
      if (!parsed.success) {
        setFilterError(
          parsed.error.issues
            .map((issue) => `${issue.path.join('.')}：${issue.message}`)
            .join('；'),
        );
        return;
      }
      if (!validatePushAnalyticsWindow(parsed.data, new Date())) {
        setFilterError('请选择最近 31 天内的有效范围；开始含、结束不含，结束不晚于当前时刻。');
        return;
      }
      setFilterError(null);
      setApplied(parsed.data);
      setSelectedNotification(null);
    } catch (error) {
      setFilterError(error instanceof Error ? error.message : '筛选条件无效');
    }
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      await pushAnalyticsApi.export(query);
      toast('已导出当前筛选的汇总 CSV（不含用户或设备明细）', { tone: 'pos' });
    } catch (error) {
      toast(error instanceof Error ? error.message : '导出失败', { tone: 'neg' });
    } finally {
      setExporting(false);
    }
  };
  const selectField = (
    key: string,
    label: string,
    options: Array<{ value: string; label: string }>,
  ) => (
    <div className="vstack" style={{ gap: 4, minWidth: 155 }} key={key}>
      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{label}</span>
      <Select
        name={key}
        value={draft[key] ?? ''}
        onChange={(event) => setField(key, event.target.value)}
      >
        <option value="">全部{label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
  const countCards = (keys: Array<keyof PushAnalyticsMetricCounts>) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        paddingTop: 14,
      }}
    >
      {keys.map((key) => {
        const metric = metrics.find((item) => item.key === key);
        if (!metric || !data) return null;
        const value = data.summary[key];
        return (
          <StatCard
            key={key}
            label={metric.label}
            value={metric.share ? ratio(value) : (value ?? '—')}
          />
        );
      })}
    </div>
  );

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: '10px 14px' }}>
        <div className="hstack" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="vstack" style={{ gap: 4 }}>
            <span className="muted" style={{ fontSize: 11.5 }}>
              消息创建日期（开始含、结束不含）
            </span>
            <DateRangeButton
              value={range}
              onChange={setRange}
              referenceDate={pushAnalyticsCalendarToday(draft.report_timezone || 'UTC')}
            />
          </div>
          <div className="vstack" style={{ gap: 4, width: 220 }}>
            <span className="muted" style={{ fontSize: 11.5 }}>
              统计时区（IANA）
            </span>
            <Select
              name="report_timezone"
              searchable
              value={draft.report_timezone}
              onChange={(event) => setField('report_timezone', event.target.value)}
            >
              {timezoneOptions.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </Select>
          </div>
          <SearchInput
            width={260}
            value={draft.notification_id ?? ''}
            placeholder="精确 notification_id（UUID）"
            onChange={(event) => setField('notification_id', event.target.value)}
          />
          <SearchInput
            width={215}
            value={draft.user_uid ?? ''}
            placeholder="10 位业务 UID（非内部 UUID）"
            onChange={(event) => setField('user_uid', event.target.value)}
          />
          <div className="vstack" style={{ gap: 4, width: 230 }}>
            <span className="muted" style={{ fontSize: 11.5 }}>
              模板
            </span>
            <Select
              name="template_slug"
              searchable
              searchPlaceholder="搜索模板名称"
              value={draft.template_slug ?? ''}
              onChange={(event) => setField('template_slug', event.target.value)}
            >
              <option value="">全部模板</option>
              {templateSlugs.map((slug) => (
                <option key={slug} value={slug}>
                  {formatPushTemplateName(slug)}
                </option>
              ))}
            </Select>
          </div>
          <input
            className="input mono"
            aria-label="实际渲染语言"
            placeholder="实际渲染语言，如 en"
            style={{ width: 190 }}
            value={draft.rendered_language ?? ''}
            onChange={(event) => setField('rendered_language', event.target.value)}
          />
          {selectField('notification_kind', '通知性质', [
            { value: 'MARKETING', label: '营销（OFFER）' },
            { value: 'FUNCTIONAL', label: '功能（非 OFFER）' },
          ])}
          {selectField(
            'category',
            '消息类别',
            PUSH_TEMPLATE_CATEGORIES.map((value) => ({
              value,
              label: `${PUSH_ANALYTICS_CATEGORY_LABELS[value] ?? value} (${value})`,
            })),
          )}
          {selectField(
            'message_status',
            '消息逻辑状态',
            Object.entries(MESSAGE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
          )}
          <div className="hstack" style={{ gap: 8 }}>
            <Switch
              ariaLabel="包含可识别测试数据"
              checked={includeTests}
              onChange={setIncludeTests}
            />
            <span style={{ fontSize: 12 }}>包含可识别测试数据</span>
          </div>
          <button type="button" className="btn btn-accent" onClick={applyFilters}>
            <Icons.IconFilter size={12} />
            应用筛选
          </button>
        </div>
        <div
          className="hstack"
          style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}
        >
          <span className="chip chip-info">以下仅筛选设备，不改变消息数量或归因打开</span>
          {DEVICE_FILTERS.map((field) =>
            selectField(
              field.key,
              field.label,
              field.values.map((value) => ({
                value,
                label:
                  field.key === 'delivery_status'
                    ? DELIVERY_STATUS_LABELS[value as keyof typeof DELIVERY_STATUS_LABELS]
                    : value,
              })),
            ),
          )}
        </div>
        {filterError && (
          <p role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
            {filterError}
          </p>
        )}
        <p className="muted" style={{ fontSize: 11.5 }}>
          当前已应用窗口{' '}
          <span className="mono" title={`[${query.start_at}, ${query.end_at})`}>
            [{time(query.start_at)}, {time(query.end_at)})
          </span>
          ；今日结束截到应用筛选时刻。所有区块和汇总 CSV
          使用相同已应用筛选，改动后请点「应用筛选」。
        </p>
      </div>
      <div
        className="card-flat"
        style={{ padding: 14, background: 'var(--warn-soft)', fontSize: 12, lineHeight: 1.6 }}
      >
        当前客户端存在后台通知可展示但点击未回传的缺口。「尚未记录打开」不证明用户没有点击；已读不等于打开。本分析不修复客户端点击链路。逻辑分派、供应商接受、记录打开不构成逐级递减漏斗，消息已记录打开与部分设备失败可以同时存在。
      </div>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn"
          disabled={overview.isFetching || notifications.isFetching}
          onClick={() => {
            void overview.refetch();
            void notifications.refetch();
          }}
        >
          <Icons.IconRefresh size={12} />
          刷新当前窗口
        </button>
        <button
          type="button"
          className="btn"
          disabled={!data || overview.isError || overview.isFetching || exporting}
          onClick={() => void exportCsv()}
        >
          <Icons.IconDownload size={12} />
          {exporting ? '导出中…' : '导出汇总 CSV'}
        </button>
        <span className="muted" style={{ fontSize: 11.5 }}>
          默认排除当前可识别测试数据；内部账号标签增删会影响历史筛选，匿名化后未知测试属性不能保证排除。
        </span>
      </div>
      {overview.isPending && (
        <p role="status" className="muted">
          推送分析加载中…
        </p>
      )}
      {overview.isError && (
        <div role="alert" className="card-flat" style={{ padding: 14, color: 'var(--neg)' }}>
          聚合加载失败 · {overview.error.message}。请重试，或缩小查询范围。
          <button className="btn btn-sm" type="button" onClick={() => void overview.refetch()}>
            重试聚合
          </button>
        </div>
      )}
      {data && !overview.isError && (
        <>
          <Section
            title="逻辑消息概览"
            sub="消息按 notification_id 去重；已分派以 sent_at 非空为准，打开以 opened_at 非空为准。打开率 = 已分派且打开 / 已分派。"
          >
            {countCards([
              'message_created',
              'message_dispatched',
              'message_opened',
              'dispatched_opened',
              'opened_without_dispatch',
              'open_rate',
            ])}
            {data.summary.opened_without_dispatch > 0 && (
              <p className="chip chip-warn">
                发现 <span className="mono">{data.summary.opened_without_dispatch}</span>{' '}
                条无分派却记录打开，单列且不进入打开率分子。
              </p>
            )}
          </Section>
          <Section
            title="设备投递概览"
            sub="仅当前设备记录与已应用设备筛选；SENT = 供应商接受，不代表手机实际收到。比例分母包含排队、发送中及失败。"
          >
            {countCards([
              'device_total',
              'provider_accepted',
              'provider_acceptance_share',
              'device_pending',
              'device_sending',
              'device_failed_retriable',
              'device_failed_permanent',
            ])}
          </Section>
          <Section
            title="消息创建 cohort 日趋势"
            sub={`按 ${data.meta.report_timezone} 的消息创建日分桶；展示同一创建批次后续当前结果，不是打开活动日。`}
          >
            {data.trend.length ? (
              <Charts.LineChartX
                height={220}
                xLabels={data.trend.map((row) => row.date)}
                series={[
                  {
                    label: '创建消息',
                    color: 'var(--chart-1)',
                    values: data.trend.map((row) => row.message_created),
                  },
                  {
                    label: '逻辑分派',
                    color: 'var(--chart-2)',
                    values: data.trend.map((row) => row.message_dispatched),
                  },
                  {
                    label: '记录打开',
                    color: 'var(--chart-3)',
                    values: data.trend.map((row) => row.message_opened),
                  },
                  {
                    label: '供应商接受（设备）',
                    color: 'var(--chart-4)',
                    values: data.trend.map((row) => row.provider_accepted),
                  },
                ]}
              />
            ) : (
              <p className="muted" style={{ padding: '14px 0' }}>
                当前创建窗口无消息。
              </p>
            )}
          </Section>
          <Section
            title="模板 × 实际渲染语言"
            sub="使用历史模板 slug 与 rendered_language 快照；不读取用户当前语言，不推测运营任务归因。"
          >
            <Table className="tbl">
              <thead>
                <tr>
                  <th>模板</th>
                  <th>实际语言</th>
                  {metrics.map((metric) => (
                    <th key={metric.key}>{metric.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.templates.map((row) => (
                  <tr key={`${row.template_slug}:${row.rendered_language}`}>
                    <td title={row.template_slug}>{formatPushTemplateName(row.template_slug)}</td>
                    <td>{fmtLang(row.rendered_language)}</td>
                    {metrics.map((metric) => (
                      <td className="mono" key={metric.key}>
                        {metric.share ? ratio(row[metric.key]) : number(row[metric.key] ?? 0)}
                      </td>
                    ))}
                  </tr>
                ))}
                {data.templates.length === 0 && (
                  <tr>
                    <td
                      colSpan={metrics.length + 2}
                      style={{ padding: 24, color: 'var(--fg-muted)' }}
                    >
                      当前筛选无模板汇总。
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Section>
          <Section
            title="统计时刻与覆盖限制"
            sub="近期主表分析；不含 R2 归档，与通知列表和详情独立读取。"
          >
            <p className="mono">
              统计时刻 {time(data.meta.statistics_at)} · 可查起点{' '}
              {time(data.meta.coverage_start_at)} · 缓存 {data.meta.cache_hit ? '命中' : '未命中'} /
              最多 {data.meta.cache_ttl_seconds} 秒
            </p>
            <p>
              仅支持近 <span className="mono">{data.meta.lookback_days}</span> 天；消息保留/归档策略{' '}
              <span className="mono">{data.meta.retention_days}</span> 天不等于可查询完整历史。
            </p>
            <p>
              匿名化消息 <span className="mono">{data.meta.anonymized_messages}</span> ·
              测试分类未知{' '}
              <span className="mono">{data.meta.test_classification_unknown_messages}</span> ·
              可识别设备明细清理下界{' '}
              <span className="mono">{data.meta.delivery_history_cleared_messages}</span>
            </p>
            {data.meta.coverage_notes.map((note) => (
              <p key={note} className="muted" style={{ fontSize: 11.5 }}>
                {note}
              </p>
            ))}
            <Table className="tbl">
              <thead>
                <tr>
                  <th>不可用指标</th>
                  <th>原因</th>
                </tr>
              </thead>
              <tbody>
                {data.unavailable_metrics.map((metric) => (
                  <tr key={metric.key}>
                    <td>
                      {metric.label} <span className="chip">不可用</span>
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>{metric.reason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </>
      )}
      <Section
        title="通知明细"
        sub="支持精确 notification_id / 10 位业务 UID 查询；设备筛选仅改变设备摘要。列表与聚合快照可能不同，点击查看全部设备。"
      >
        {notifications.isPending && (
          <p role="status" className="muted">
            通知明细加载中…
          </p>
        )}
        {notifications.isError && (
          <div role="alert" style={{ padding: '14px 0', color: 'var(--neg)' }}>
            通知明细加载失败 · {notifications.error.message}
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void notifications.refetch()}
            >
              重试明细
            </button>
          </div>
        )}
        {notifications.data && !notifications.isError && (
          <>
            <Table className="tbl">
              <thead>
                <tr>
                  <th>消息 / 业务 UID</th>
                  <th>App</th>
                  <th>模板 / 类别 / 语言</th>
                  <th>当前逻辑状态</th>
                  <th>创建 / 逻辑分派</th>
                  <th>首次已读 / 记录打开</th>
                  <th>设备摘要</th>
                  <th>数据分类</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {notifications.data.data.map((notification) => (
                  <tr key={notification.notification_id}>
                    <td className="mono">
                      {notification.notification_id}
                      <br />
                      {notification.user_uid ?? '身份已匿名化或关联已清理'}
                    </td>
                    <td>{notification.app_name}</td>
                    <td>
                      <span title={notification.template_slug}>
                        {formatPushTemplateName(notification.template_slug)}
                      </span>
                      <br />
                      {notification.category} · {fmtLang(notification.rendered_language)}
                    </td>
                    <td>
                      <span className="chip">{MESSAGE_STATUS_LABELS[notification.status]}</span>
                    </td>
                    <td className="mono">
                      {time(notification.created_at)}
                      <br />
                      {time(notification.sent_at)}
                    </td>
                    <td className="mono">
                      {time(notification.read_at)}
                      <br />
                      {notification.opened_at ? time(notification.opened_at) : '尚未记录打开'}
                    </td>
                    <td>
                      {notification.device_summary ? (
                        <span className="mono">
                          {notification.device_summary.device_total} 条 / 接受{' '}
                          {notification.device_summary.provider_accepted}
                          <br />
                          可重试 {notification.device_summary.device_failed_retriable} / 永久{' '}
                          {notification.device_summary.device_failed_permanent}
                        </span>
                      ) : (
                        '设备明细已因隐私清理'
                      )}
                      <br />
                      <span title={deviceDataLabel(notification)} className="chip">
                        {notification.device_data_state === 'AVAILABLE'
                          ? '存在记录，可能不完整'
                          : notification.device_data_state === 'NO_RECORDS'
                            ? '暂无设备投递记录'
                            : '隐私清理'}
                      </span>
                    </td>
                    <td>{TEST_KIND_LABELS[notification.test_data_kind]}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          setSelectedNotification({
                            id: notification.notification_id,
                            scope: appId,
                          })
                        }
                      >
                        查看通知详情
                      </button>
                    </td>
                  </tr>
                ))}
                {notifications.data.data.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: 24, color: 'var(--fg-muted)' }}>
                      当前筛选无通知；不代表历史从未创建。
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Pagination
              total={notifications.data.total}
              page={page}
              pageSize={paging.limit}
              onChange={(nextPage) => setPaging({ ...paging, signature, page: nextPage })}
              onPageSizeChange={(limit) => setPaging({ signature, page: 1, limit })}
              sizeOptions={[20, 50, 100]}
            />
          </>
        )}
      </Section>
      {selectedId && (
        <PushAnalyticsNotificationDetail
          key={`${selectedId}:${appId}`}
          id={selectedId}
          appId={appId !== 'all' ? appId : undefined}
          timezone={query.report_timezone}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </div>
  );
}
